/**
 * localStorage-backed frecency suggestion engine for hand-typed batch-upload
 * hints (spec §5.4). Pure module functions with an injectable `storage` +
 * `now` seam — no React/DOM dependency assumed beyond the optional default
 * `window.localStorage` lookup. Deliberately simple/predictable: no
 * similarity or NLP scoring.
 */

import { reportHandledException } from '@/lib/error-reporting'
import { getActiveContext } from '@kiao-voice/api-client'

const RECENT_STORAGE_KEY_PREFIX = 'kiao.batch-hint-recent.v1'
const COLLAPSED_STORAGE_KEY_PREFIX = 'kiao.batch-hint-suggestions-collapsed.v1'
const WORKSPACE_INDEX_KEY = 'kiao.batch-hint-workspaces.v1'

function workspaceStorageKey(prefix: string, workspace = getActiveContext()): string {
  return `${prefix}.${encodeURIComponent(workspace)}`
}

function rememberWorkspace(storage: HintStorageLike): void {
  try {
    const workspace = getActiveContext()
    const raw = storage.getItem(WORKSPACE_INDEX_KEY)
    const existing = raw ? (JSON.parse(raw) as unknown) : []
    const workspaces = Array.isArray(existing)
      ? existing.filter((value): value is string => typeof value === 'string')
      : []
    if (!workspaces.includes(workspace)) {
      storage.setItem(WORKSPACE_INDEX_KEY, JSON.stringify([...workspaces, workspace]))
    }
  } catch (error) {
    reportHandledException(error, { source: 'features/dictionary/hint-suggestions.ts' })
    warnStorageUnavailableOnce(error)
  }
}

/** Upper bound on stored recent-hint entries; the lowest-frecency entry is
 *  evicted once a new/updated entry would exceed this. */
export const MAX_RECENT_HINT_ENTRIES = 100

/** Frecency half-life, in days: a term's score halves every 30 days of disuse. */
const FRECENCY_HALF_LIFE_DAYS = 30

const DEFAULT_SUGGESTION_LIMIT = 8

/** `useCount` at or above this makes a suggestion eligible for the "收進詞典？"
 *  incubation nudge. */
export const INCUBATION_USE_COUNT_THRESHOLD = 3

const DAY_MS = 1000 * 60 * 60 * 24

/** One recorded hand-typed hint. `lastUsedAt` is an epoch-ms timestamp. */
export interface RecentHintEntry {
  term: string
  useCount: number
  lastUsedAt: number
}

/** Minimal storage shape this module needs — satisfied by `window.localStorage`
 *  or an injected fake in tests. */
export interface HintStorageLike {
  readonly length?: number
  key?(index: number): string | null
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export interface HintSuggestionsDeps {
  /** Defaults to `window.localStorage` when available. */
  storage?: HintStorageLike
  /** Defaults to `Date.now`. Inject a fixed clock in tests. */
  now?: () => number
}

/** Suggestion surfaced to the UI: the stored entry plus the incubation flag. */
export interface HintSuggestion {
  term: string
  useCount: number
  eligibleForDictionary: boolean
}

let hasWarnedStorageUnavailable = false

/** Test seam only: the warn-once flag is module state and leaks across tests. */
export function __resetHintSuggestionsWarningForTests(): void {
  hasWarnedStorageUnavailable = false
}

function warnStorageUnavailableOnce(error: unknown): void {
  if (hasWarnedStorageUnavailable) return
  hasWarnedStorageUnavailable = true
  // Report path (not a throw): the whole suggestions feature degrades to a
  // no-op for this session, but the actual error must reach the console so a
  // quota failure is distinguishable from Safari private mode.
  console.warn(
    '[dictionary/hint-suggestions] localStorage is unavailable — hint suggestions are disabled for this session.',
    error
  )
}

function resolveStorage(deps?: HintSuggestionsDeps): HintStorageLike | undefined {
  if (deps?.storage) return deps.storage
  // The property GETTER itself can throw (SecurityError in sandboxed iframes /
  // blocked-storage browser policies), not just the methods — same degradation
  // path as an unusable storage object.
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined
  } catch (error) {
    reportHandledException(error, { source: 'features/dictionary/hint-suggestions.ts' })
    warnStorageUnavailableOnce(error)
    return undefined
  }
}

function resolveNow(deps?: HintSuggestionsDeps): number {
  return (deps?.now ?? Date.now)()
}

function isRecentHintEntry(value: unknown): value is RecentHintEntry {
  if (!value || typeof value !== 'object') return false
  const entry = value as Record<string, unknown>
  return (
    typeof entry.term === 'string' &&
    typeof entry.useCount === 'number' &&
    typeof entry.lastUsedAt === 'number'
  )
}

function readEntries(deps?: HintSuggestionsDeps): RecentHintEntry[] {
  const storage = resolveStorage(deps)
  if (!storage) {
    warnStorageUnavailableOnce(new Error('window.localStorage is not available'))
    return []
  }
  let raw: string | null
  try {
    raw = storage.getItem(workspaceStorageKey(RECENT_STORAGE_KEY_PREFIX))
  } catch (error) {
    reportHandledException(error, { source: 'features/dictionary/hint-suggestions.ts' })
    warnStorageUnavailableOnce(error)
    return []
  }
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) throw new Error('stored value is not an array')
    return parsed.filter(isRecentHintEntry)
  } catch (error) {
    reportHandledException(error, { source: 'features/dictionary/hint-suggestions.ts' })
    // A corrupt value would otherwise disable suggestions forever — reset it
    // so the store can rebuild from the next submit.
    console.warn(
      '[dictionary/hint-suggestions] corrupt recent-hints value — resetting the store.',
      error
    )
    try {
      storage.removeItem(workspaceStorageKey(RECENT_STORAGE_KEY_PREFIX))
    } catch (removeError) {
      reportHandledException(removeError, { source: 'features/dictionary/hint-suggestions.ts' })
      warnStorageUnavailableOnce(removeError)
    }
    return []
  }
}

function writeEntries(entries: RecentHintEntry[], deps?: HintSuggestionsDeps): void {
  const storage = resolveStorage(deps)
  if (!storage) {
    warnStorageUnavailableOnce(new Error('window.localStorage is not available'))
    return
  }
  try {
    rememberWorkspace(storage)
    storage.setItem(workspaceStorageKey(RECENT_STORAGE_KEY_PREFIX), JSON.stringify(entries))
  } catch (error) {
    reportHandledException(error, { source: 'features/dictionary/hint-suggestions.ts' })
    warnStorageUnavailableOnce(error)
  }
}

/**
 * Remove everything this module persisted. Called from the web sign-out path:
 * the store is a browser-global key, and one user's hand-typed terms must not
 * surface as suggestions for the next account on a shared machine (same
 * contract as the active-context key).
 */
export function clearHintSuggestionData(deps?: HintSuggestionsDeps): void {
  const storage = resolveStorage(deps)
  if (!storage) return

  const keys = new Set<string>([
    workspaceStorageKey(RECENT_STORAGE_KEY_PREFIX),
    workspaceStorageKey(COLLAPSED_STORAGE_KEY_PREFIX),
    RECENT_STORAGE_KEY_PREFIX,
    COLLAPSED_STORAGE_KEY_PREFIX,
    WORKSPACE_INDEX_KEY,
  ])

  try {
    const raw = storage.getItem(WORKSPACE_INDEX_KEY)
    const indexed = raw ? (JSON.parse(raw) as unknown) : []
    if (Array.isArray(indexed)) {
      for (const workspace of indexed) {
        if (typeof workspace !== 'string') continue
        keys.add(workspaceStorageKey(RECENT_STORAGE_KEY_PREFIX, workspace))
        keys.add(workspaceStorageKey(COLLAPSED_STORAGE_KEY_PREFIX, workspace))
      }
    }
  } catch (error) {
    reportHandledException(error, { source: 'features/dictionary/hint-suggestions.ts' })
    warnStorageUnavailableOnce(error)
  }

  // Real localStorage is enumerable. This also catches orphaned workspace
  // entries when persisting the index failed, and recovers every entry even if
  // an externally modified/corrupt index could not be parsed.
  try {
    if (typeof storage.length === 'number' && storage.key) {
      for (let index = 0; index < storage.length; index++) {
        const key = storage.key(index)
        if (
          key?.startsWith(`${RECENT_STORAGE_KEY_PREFIX}.`) ||
          key?.startsWith(`${COLLAPSED_STORAGE_KEY_PREFIX}.`)
        ) {
          keys.add(key)
        }
      }
    }
  } catch (error) {
    reportHandledException(error, { source: 'features/dictionary/hint-suggestions.ts' })
    warnStorageUnavailableOnce(error)
  }

  // Each removal is independent: one browser-storage failure must not prevent
  // the current workspace, legacy keys, index, or another workspace from being
  // purged during sign-out.
  for (const key of keys) {
    try {
      storage.removeItem(key)
    } catch (error) {
      reportHandledException(error, { source: 'features/dictionary/hint-suggestions.ts' })
      warnStorageUnavailableOnce(error)
    }
  }
}

/** Frecency score: `useCount * 0.5 ^ (daysSinceLastUse / 30)`. */
export function frecencyScore(entry: RecentHintEntry, now: number): number {
  const daysSinceLastUse = Math.max(0, (now - entry.lastUsedAt) / DAY_MS)
  return entry.useCount * Math.pow(0.5, daysSinceLastUse / FRECENCY_HALF_LIFE_DAYS)
}

/**
 * Record one use of a hand-typed hint (called by the caller ONLY on
 * successful submit — timing is the caller's responsibility, not this
 * module's). Creates a new entry (`useCount: 1`) or increments an existing
 * one and refreshes `lastUsedAt`. Evicts the lowest-frecency entry once the
 * store would exceed `MAX_RECENT_HINT_ENTRIES`.
 */
export function recordHintUsage(term: string, deps?: HintSuggestionsDeps): void {
  const normalized = term.trim()
  if (!normalized) return

  const now = resolveNow(deps)
  const entries = readEntries(deps)
  const index = entries.findIndex((entry) => entry.term === normalized)

  if (index >= 0) {
    entries[index] = { ...entries[index], useCount: entries[index].useCount + 1, lastUsedAt: now }
  } else {
    entries.push({ term: normalized, useCount: 1, lastUsedAt: now })
  }

  if (entries.length > MAX_RECENT_HINT_ENTRIES) {
    entries.sort((a, b) => frecencyScore(b, now) - frecencyScore(a, now))
    entries.length = MAX_RECENT_HINT_ENTRIES
  }

  writeEntries(entries, deps)
}

/** Drop a term from the recent store — called after it "graduates" into a
 *  dictionary (§5.3), so it stops being suggested from local recency. */
export function removeHintTerm(term: string, deps?: HintSuggestionsDeps): void {
  const entries = readEntries(deps)
  const filtered = entries.filter((entry) => entry.term !== term)
  if (filtered.length !== entries.length) {
    writeEntries(filtered, deps)
  }
}

/**
 * Top-N suggestions by frecency score, excluding any term already visible on
 * screen (chips from selected dictionaries or hand-typed). Defaults to 8.
 */
export function getHintSuggestions(
  options: { limit?: number; exclude?: string[] } = {},
  deps?: HintSuggestionsDeps
): HintSuggestion[] {
  const now = resolveNow(deps)
  const limit = options.limit ?? DEFAULT_SUGGESTION_LIMIT
  const excluded = new Set(options.exclude ?? [])

  return readEntries(deps)
    .filter((entry) => !excluded.has(entry.term))
    .sort((a, b) => frecencyScore(b, now) - frecencyScore(a, now))
    .slice(0, limit)
    .map((entry) => ({
      term: entry.term,
      useCount: entry.useCount,
      eligibleForDictionary: entry.useCount >= INCUBATION_USE_COUNT_THRESHOLD,
    }))
}

/** Whether the suggestions row is collapsed (persisted UI preference). */
export function isHintSuggestionsCollapsed(deps?: HintSuggestionsDeps): boolean {
  const storage = resolveStorage(deps)
  if (!storage) {
    warnStorageUnavailableOnce(new Error('window.localStorage is not available'))
    return false
  }
  try {
    return storage.getItem(workspaceStorageKey(COLLAPSED_STORAGE_KEY_PREFIX)) === '1'
  } catch (error) {
    reportHandledException(error, { source: 'features/dictionary/hint-suggestions.ts' })
    warnStorageUnavailableOnce(error)
    return false
  }
}

/** Persist the suggestions row's collapsed/expanded state. */
export function setHintSuggestionsCollapsed(collapsed: boolean, deps?: HintSuggestionsDeps): void {
  const storage = resolveStorage(deps)
  if (!storage) {
    warnStorageUnavailableOnce(new Error('window.localStorage is not available'))
    return
  }
  try {
    if (collapsed) {
      rememberWorkspace(storage)
      storage.setItem(workspaceStorageKey(COLLAPSED_STORAGE_KEY_PREFIX), '1')
    } else {
      storage.removeItem(workspaceStorageKey(COLLAPSED_STORAGE_KEY_PREFIX))
    }
  } catch (error) {
    reportHandledException(error, { source: 'features/dictionary/hint-suggestions.ts' })
    warnStorageUnavailableOnce(error)
  }
}
